import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import axiosInstance from '../../../Config/axiosInstance';
import AddSuppliersContract from './AddSuppliersContract';
import EditSuppliersContract from './EditSuppliersContract';
import './SuppliersContract.css';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

function SuppliersContract() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [canDelete, setCanDelete] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  // Funcție pentru formatarea datei în format românesc cu PUNCTE
  const formatDateToRomanian = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    // Schimbăm slash-ul cu punct
    return `${day}.${month}.${year}`;
  };

  //Verificarea permisiunilor
  useEffect(() => {
    const requiredPath = "/supplier-contracts";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasCreatePermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_create === true;
    });

    setCanCreate(hasCreatePermission);

    const hasEditPermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_edit === true;
    });

    setCanEdit(hasEditPermission);
    setCanDelete(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_delete));
  }, []);

  //Functii de navigare
  const handleAddClick = () => navigate('/supplier-contracts/add-supplier-contracts');
  const handleEditClick = (row) => navigate(`/supplier-contracts/edit-supplier-contracts/${row.id}`);

  //Stergere
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };
  //Obtinerea datelor din backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contractsResponse, suppliersResponse] = await Promise.all([
          axiosInstance.get('/supplier-contracts/'),
          axiosInstance.get('/suppliers/')
        ]);

        const supplierMap = suppliersResponse.data.reduce((acc, supplier) => {
          acc[supplier.id] = supplier.name;
          return acc;
        }, {});

        const sortedData = contractsResponse.data.map((item) => ({
          id: item.id,
          contract_number: item.contract_number,
          contract_date: item.contract_date, // Păstrăm valoarea originală pentru sortare
          expire_date: item.expire_date, // Păstrăm valoarea originală pentru sortare
          provider_contract_nr: item.provider_contract_nr,
          contract_description: item.contract_description,
          alert_days: item.alert_days,
          status: item.status,
          supplier: supplierMap[item.supplier] || "Unknown",
          remarks: item.remarks,
        })).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, [location.pathname]);

  //Definirea culorilor pentru statusuri
  const statusColors = {
    New: '#9e9e9e',       // Portocaliu
    Signed: '#4caf50',    // Verde
    Expired: '#f44336',   // Roșu
    Closed: '#ff9800',    // Gri
  };

  //Definirea coloanelor
  const columns = [
    {
      field: 'contract_number',
      headerName: 'PMPC Contract Nr.',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'contract_date',
      headerName: 'Contract Date',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatDateToRomanian(params.value)}
        </div>
      ),
      // Adăugăm sorting comparer pentru a sorta corect după data originală
      sortComparator: (v1, v2) => {
        const date1 = new Date(v1);
        const date2 = new Date(v2);
        return date1.getTime() - date2.getTime();
      }
    },
    {
      field: 'expire_date',
      headerName: 'Expire Date',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatDateToRomanian(params.value)}
        </div>
      ),
      // Adăugăm sorting comparer pentru a sorta corect după data originală
      sortComparator: (v1, v2) => {
        const date1 = new Date(v1);
        const date2 = new Date(v2);
        return date1.getTime() - date2.getTime();
      }
    },
    {
      field: 'provider_contract_nr',
      headerName: 'Provider Contract Nr.',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'contract_description',
      headerName: 'Contract Description',
      flex: 2.5,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'alert_days',
      headerName: 'Alert (Days)',
      flex: 0.7,
      minWidth: 70,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <span
            style={{
              backgroundColor: statusColors[params.value] || '#ccc',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '10px',
              width: '65px',
              maxWidth: '65px',
              minWidth: '65px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '15px'
            }}
          >
            {params.value}
          </span>
        </div>
      )
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
  ];

  //Adaugarea coloanei de editare/stergere doar daca utilizatorul are permisiune
  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route path="/" element={
        <div className="supplier-contracts-page">
          <div className="supplier-contracts-container">
            <h2 className='title'>SUPPLIER CONTRACT</h2>
            {/* Afisarea butonului doar daca utilizatorulare permisiune */}
            {canCreate && (
              <div className="add-button-container">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  disabled={!canCreate}
                  sx={{ float: 'right', marginBottom: '40px' }}
                >
                  New Contract
                </Button>
              </div>
            )}
          </div>
          <div className="table-container">
            <DataGrid
              id="supplier-contract-table"
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />


          </div>
          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete this record?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenDeleteDialog(false)}
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: '#424242',
                  '&:hover': {
                    backgroundColor: '#bdbdbd',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await axiosInstance.delete(`/supplier-contracts/${itemToDelete.id}/`);
                    setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                    setOpenDeleteDialog(false);
                  } catch (error) {
                    const message =
                      error.response?.data?.error ||
                      error.response?.data?.detail ||
                      "An unexpected error occurred while deleting.";
                    setDeleteErrorMessage(message);
                    setOpenDeleteDialog(false);  // Închide dialogul de confirmare
                    setErrorDialogOpen(true);    // Deschide dialogul de eroare
                  }
                }}
                sx={{
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  }
                }}
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dialog pentru eroare */}
          <Dialog
            open={errorDialogOpen}
            onClose={() => setErrorDialogOpen(false)}
            aria-labelledby="error-dialog-title"
            aria-describedby="error-dialog-description"
          >
            <DialogTitle id="error-dialog-title" style={{ color: 'red' }}>Delete Failed</DialogTitle>
            <DialogContent>
              <DialogContentText id="error-dialog-description">
                {deleteErrorMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setErrorDialogOpen(false)}
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: '#424242',
                  '&:hover': {
                    backgroundColor: '#bdbdbd',
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

        </div>
      } />

      {/* Structura subrutelor */}
      <Route path="add-supplier-contracts" element={<AddSuppliersContract />} />
      <Route path="edit-supplier-contracts/:id" element={<EditSuppliersContract />} />
    </Routes>
  );
}

export default SuppliersContract;